import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Grid, MenuItem, Paper, TextField, Typography } from "@material-ui/core";
import { cloError, cloGet, ENDPOINTS, formatDate } from "./api";
import {
  armMixRows, armSeries, campaignRows, cohortNames, CohortOverview, cohortSeries, funnelRows, noCohortSeries,
  panelSplitRows, sum, timelineRows,
} from "./cohortOverview";
import {
  ArmMixChart, CampaignCohortChart, ChartCard, EmptyChart, FunnelChart, PanelSplitChart, PlacementsChart, useChartTheme,
} from "./CohortOverviewCharts";
import { CampaignCohortTable, PanelTable } from "./CohortOverviewTables";

const DAY_OPTIONS = [7, 30, 90];

const NumbersToggle: React.FC = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box mt={1}>
      <Button size="small" color="primary" onClick={() => setOpen(!open)}>
        {open ? "Hide the numbers" : "Show the numbers"}
      </Button>
      {open && <Box mt={1} style={{ overflowX: "auto" }}>{children}</Box>}
    </Box>
  );
};

const OverviewCharts: React.FC<{ overview: CohortOverview; days: number }> = ({ overview, days }) => {
  const theme = useChartTheme();
  const series = useMemo(() => cohortSeries(cohortNames(overview), theme.mode), [overview, theme.mode]);
  const campaigns = campaignRows(overview.campaigns, series);
  const split = panelSplitRows(overview.panels);
  const mix = armMixRows(overview.panels, series);
  const timeline = timelineRows(overview.timeline);
  const anyPlaced = campaigns.some((row) => row.total > 0);
  const changes = {
    placed: sum(overview.timeline.map((d) => d.placed)),
    moved: sum(overview.timeline.map((d) => d.moved)),
    exited: sum(overview.timeline.map((d) => d.exited)),
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <ChartCard
          title="Customers at each stage"
          subtitle="Each stage is counted on its own; percentages are of customers with live consent. Seeing an offer means in the test arm of a live retailer panel for a live campaign, with offers not hidden."
        >
          <FunnelChart rows={funnelRows(overview.funnel)} theme={theme} />
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard
          title="Placements per day"
          subtitle={`Last ${days} days, UTC: ${changes.placed} placed, ${changes.moved} moved between cohorts, ${changes.exited} left a cohort.`}
        >
          {changes.placed + changes.moved + changes.exited > 0
            ? <PlacementsChart rows={timeline} theme={theme} />
            : <EmptyChart text={`No cohort changes in the last ${days} days.`} />}
        </ChartCard>
      </Grid>
      <Grid item xs={12}>
        <ChartCard title="Cohorts by campaign" subtitle="Customers per cohort today, by retailer and campaign number.">
          {anyPlaced
            ? <CampaignCohortChart rows={campaigns} series={[...series, noCohortSeries(theme.mode)]} theme={theme} />
            : <EmptyChart text="Nobody is placed in a cohort yet." />}
          <NumbersToggle>
            <CampaignCohortTable campaigns={overview.campaigns} series={series} />
          </NumbersToggle>
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Test and control per retailer panel" subtitle="Current members of each live panel.">
          {split.some((row) => row.total > 0)
            ? <PanelSplitChart rows={split} series={armSeries(theme.mode)} theme={theme} />
            : <EmptyChart text="No live panel has members yet." />}
          {overview.panels.length > 0 && (
            <NumbersToggle>
              <PanelTable panels={overview.panels} />
            </NumbersToggle>
          )}
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Cohort mix in each arm" subtitle="Share of each arm by the cohort customers were in when drawn.">
          {mix.length > 0
            ? <ArmMixChart rows={mix} series={series} theme={theme} />
            : <EmptyChart text="No live panel has members yet." />}
        </ChartCard>
      </Grid>
    </Grid>
  );
};

const CohortOverviewTab: React.FC = () => {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState<CohortOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const res = await cloGet(ENDPOINTS.CLO_COHORT_STATS, { days });
      if (request === latest.current) setOverview(res.data);
    } catch (error_) {
      if (request === latest.current) setError(cloError(error_));
    } finally {
      if (request === latest.current) setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (!overview) {
    return (
      <Paper>
        <Box p={4} display="flex" flexDirection="column" alignItems="center" style={{ gap: 12 }}>
          {loading && <CircularProgress />}
          {!loading && error && (
            <>
              <Typography color="error">Could not load the cohort overview: {error}</Typography>
              <Button variant="outlined" color="primary" onClick={load}>Try again</Button>
            </>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Box>
      <Box mb={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
        <Typography variant="body2" color="textSecondary">Counts as of {formatDate(overview.generatedAt)}</Typography>
        <Box flexGrow={1} />
        <TextField
          select
          size="small"
          variant="outlined"
          label="Placements over"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ minWidth: 160 }}
        >
          {DAY_OPTIONS.map((d) => <MenuItem key={d} value={d}>Last {d} days</MenuItem>)}
        </TextField>
        <Button variant="outlined" color="primary" onClick={load} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : "Refresh"}
        </Button>
      </Box>
      {error && (
        <Box mb={2}>
          <Typography color="error">Could not refresh, showing the last counts: {error}</Typography>
        </Box>
      )}
      {overview.campaigns.length === 0
        ? (
          <Paper>
            <EmptyChart text="No CLO campaigns yet. The charts fill in once a campaign has customers in cohorts." />
          </Paper>
        )
        : <OverviewCharts overview={overview} days={days} />}
    </Box>
  );
};

export default CohortOverviewTab;
